import React, { useState, useEffect, useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import toast from 'react-hot-toast';
import {
  Plus,
  Search,
  Edit,
  Trash2,
  MapPin,
  Building,
  Eye,
  AlertCircle,
  Download,
  ArrowUpDown,
  ChevronUp,
  ChevronDown,
  X
} from 'lucide-react';
import { exportToCSV, generateExportFilename, downloadFromBackend } from '../../../utils/exportUtils';
import { formatDate, formatDateRelative } from '../../../utils/stringUtils';
import locationService from '../../../api/services/locationService';
import PermissionGate from '../../../components/common/PermissionGate/PermissionGate';
import ExportModal from '../../../components/common/ExportModal';
import Button from '../../../components/common/Button/Button';
import { Country, State, City } from 'country-state-city';
import LocationSelect from '../../../components/common/LocationSelect/LocationSelect';
import DateRangeFilter from '../../../components/common/DateRangeFilter';

const LocationManagement = () => {
  // Toast handlers
  const showSuccess = (message) => toast.success(message);
  const showError = (message) => toast.error(message);
  const showWarning = (message) => toast(message, { icon: '⚠️' });

  // State management
  const [searchTerm, setSearchTerm] = useState('');
  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState('');
  const [filters, setFilters] = useState({
    isActive: 'all'
  });
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [dateRange, setDateRange] = useState({ startDate: '', endDate: '' });

  // Debounce search term
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearchTerm(searchTerm);
    }, 500);
    return () => clearTimeout(timer);
  }, [searchTerm]);

  useEffect(() => {
    setCurrentPage(1);
  }, [debouncedSearchTerm, filters.isActive, dateRange.startDate, dateRange.endDate]);

  const [showAddModal, setShowAddModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [showViewModal, setShowViewModal] = useState(false);
  const [selectedLocation, setSelectedLocation] = useState(null);
  const [selectedRows, setSelectedRows] = useState(new Set());

  const [showExportModal, setShowExportModal] = useState(false);
  const [showBulkDeleteModal, setShowBulkDeleteModal] = useState(false);
  const [exportLoading, setExportLoading] = useState(false);

  const [sortConfig, setSortConfig] = useState({ key: null, direction: 'asc' });

  const requestSort = (key) => {
    let direction = 'asc';
    if (sortConfig.key === key && sortConfig.direction === 'asc') {
      direction = 'desc';
    }
    setSortConfig({ key, direction });
  };

  const getSortIcon = (key) => {
    if (sortConfig.key !== key) {
      return <ArrowUpDown className="h-3 w-3 ml-1 text-gray-400 group-hover:text-gray-600 transition-colors" />;
    }
    return sortConfig.direction === 'asc' ?
      <ChevronUp className="h-3 w-3 ml-1 text-[#0F2942]" /> :
      <ChevronDown className="h-3 w-3 ml-1 text-[#0F2942]" />;
  };



  // Location dropdown options
  const [countryOptions, setCountryOptions] = useState([]);
  const [stateOptions, setStateOptions] = useState([]);
  const [cityOptions, setCityOptions] = useState([]);

  const initialFormData = {
    name: '',
    code: '',
    country: null, // Changed from string 'India' to object or null
    address: {
      street: '',
      city: null, // Changed from string to object or null
      state: null, // Changed from string to object or null
      postalCode: '',
      fullAddress: ''
    },
    coordinates: {
      latitude: '',
      longitude: ''
    },
    capacity: '',
    description: '',
    facilities: [], // Added facilities array to state
    isActive: true
  };

  const [formData, setFormData] = useState(initialFormData);


  // Validation state
  const [validationErrors, setValidationErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);


  // Filter options

  // Define field types and limits
  // Define field types and limits
  const fieldConfig = {
    // Text fields: alphanumeric, spaces, comma
    name: { type: 'text', limit: 30 },
    'address.street': { type: 'text', limit: 200 },
    capacity: { type: 'number', limit: 5 },
    'address.postalCode': { type: 'text', limit: 15 },
  };

  // Validation functions
  // Validation functions
  const validateValues = (formData) => {
    const errors = {};

    // Name validation - required
    if (!formData.name.trim()) {
      errors.name = 'Location name is required';
    }

    // Optional fields validation - only validate if they have a value
    // Postal Code validation - max 15 chars, alphanumeric
    if (formData.address.postalCode && formData.address.postalCode.trim()) {
      if (formData.address.postalCode.length > 15) {
        errors.postalCode = 'Postal Code cannot exceed 15 characters';
      } else if (!/^[a-zA-Z0-9\s]+$/.test(formData.address.postalCode.trim())) {
        errors.postalCode = 'Postal Code must contain only letters and numbers';
      }
    }

    // Description validation - keep existing (500 char limit, optional)
    if (formData.description && formData.description.length > 500) {
      errors.description = 'Description cannot exceed 500 characters';
    }

    return errors;
  };

  const validateForm = () => {
    const errors = validateValues(formData);
    setValidationErrors(errors);
    return Object.keys(errors).length === 0;
  };

  // Field-level validation handler
  const handleFieldBlur = (e) => {
    const { name, value } = e.target;
    // Helper to get nested value if needed, though name is sufficient for validation logic lookup in many cases
    // We will validate the specific field
    let error = '';

    // Create a temporary errors object to merge partial validation results
    const currentErrors = validateValues(formData);

    // Extract error for the specific field name
    // Note: formData names like 'address.street' don't match simple keys in validation logic if not handled carefully.
    // However, our validateValues returns keys like 'street', 'postalCode' etc. based on checking properties.
    // We need to map input names to validation error keys.

    let errorKey = name;
    if (name === 'address.street') errorKey = 'street';
    if (name === 'address.postalCode') errorKey = 'postalCode';

    if (currentErrors[errorKey]) {
      setValidationErrors(prev => ({
        ...prev,
        [errorKey]: currentErrors[errorKey]
      }));
    } else {
      // Clear error if valid
      setValidationErrors(prev => ({
        ...prev,
        [errorKey]: ''
      }));
    }
  };

  const clearValidationErrors = () => {
    setValidationErrors({});
  };

  const getFieldError = (fieldName) => {
    return validationErrors[fieldName] || '';
  };

  const getFieldClassName = (fieldName) => {
    const hasError = validationErrors[fieldName];
    return `input-field ${hasError ? 'border-red-300 bg-red-50' : ''}`;
  };

  // Map frontend filter to backend API parameter
  const getIsActiveParam = (filterValue) => {
    switch (filterValue) {
      case 'active':
        return 'true';
      case 'inactive':
        return 'false';
      case 'all':
      default:
        return 'all';
    }
  };

  // Fetch locations using React Query
  const {
    data: locationsData,
    isLoading: loading,
    error: queryError,
    refetch: refetchLocations
  } = useQuery({
    queryKey: ['locations', filters.isActive, debouncedSearchTerm, currentPage, pageSize, dateRange.startDate, dateRange.endDate],
    queryFn: async () => {
      console.log('🔄 Fetching locations with filters:', filters, 'search:', debouncedSearchTerm, 'dateRange:', dateRange);

      const params = {
        isActive: getIsActiveParam(filters.isActive),
        page: currentPage,
        limit: pageSize,
        startDate: dateRange.startDate,
        endDate: dateRange.endDate
      };

      // Add search parameter if search term exists
      if (debouncedSearchTerm.trim()) {
        params.search = debouncedSearchTerm.trim();
      }

      const result = await locationService.getLocations(params);
      console.log('📦 Locations API result:', result);

      if (result.success) {
        // Handle different response structures
        const responseData = result.data?.data || result.data || {};
        const locations = responseData.locations || [];
        const pagination = responseData.pagination || {
          currentPage,
          totalPages: 1,
          totalCount: locations.length,
          hasNextPage: false,
          hasPrevPage: false
        };

        console.log('✅ Locations loaded:', locations.length);
        return { locations, pagination };
      } else {
        throw new Error(result.message || 'Failed to fetch locations');
      }
    },
    staleTime: 0,
    retry: 2,
  });

  const locations = locationsData?.locations || [];
  const pagination = locationsData?.pagination || {
    currentPage,
    totalPages: 1,
    totalCount: locations.length,
    hasNextPage: false,
    hasPrevPage: false
  };
  const error = queryError?.message || null;

  // Client-side sorting of current page data to match AttendanceLogs behavior
  const filteredLocations = useMemo(() => {
    let sortableLocs = [...locations];
    if (sortConfig.key !== null) {
      sortableLocs.sort((a, b) => {
        let aValue, bValue;
        switch (sortConfig.key) {
          case 'name':
            aValue = (a.name || '').toLowerCase();
            bValue = (b.name || '').toLowerCase();
            break;
          case 'state':
            aValue = (a.address?.state?.name || a.address?.state || '').toLowerCase();
            bValue = (b.address?.state?.name || b.address?.state || '').toLowerCase();
            break;
          case 'capacity':
            aValue = parseInt(a.capacity) || 0;
            bValue = parseInt(b.capacity) || 0;
            break;
          case 'isActive':
            aValue = a.isActive ? 1 : 0;
            bValue = b.isActive ? 1 : 0;
            break;
          case 'createdAt':
            aValue = new Date(a.createdAt || 0).getTime();
            bValue = new Date(b.createdAt || 0).getTime();
            break;
          case 'updatedAt':
            aValue = new Date(a.updatedAt || 0).getTime();
            bValue = new Date(b.updatedAt || 0).getTime();
            break;
          default:
            aValue = a[sortConfig.key];
            bValue = b[sortConfig.key];
        }

        if (aValue < bValue) return sortConfig.direction === 'asc' ? -1 : 1;
        if (aValue > bValue) return sortConfig.direction === 'asc' ? 1 : -1;
        return 0;
      });
    }
    return sortableLocs;
  }, [locations, sortConfig]);

  const startItem = pagination.totalCount === 0 ? 0 : ((pagination.currentPage - 1) * pageSize) + 1;
  const endItem = pagination.totalCount === 0 ? 0 : startItem + filteredLocations.length - 1;
  const totalPages = Math.max(pagination.totalPages, 1);
  const showPaginationControls = pagination.totalCount >= 20;

  const handlePageSizeChange = (event) => {
    const nextSize = Number(event.target.value);
    setPageSize(nextSize);
    setCurrentPage(1);
  };

  const canGoPrev = pagination.hasPrevPage && pagination.currentPage > 1;
  const canGoNext = pagination.hasNextPage && pagination.currentPage < totalPages;

  // --- Location Data Loading & Handlers ---

  // Load all countries on component mount
  useEffect(() => {
    const allCountries = Country.getAllCountries();
    const formattedCountries = allCountries.map(c => ({
      id: c.isoCode,
      name: c.name,
      isoCode: c.isoCode,
      ...c
    }));
    setCountryOptions(formattedCountries);
  }, []);

  // Update state options when country changes
  useEffect(() => {
    if (formData.country) {
      const states = State.getStatesOfCountry(formData.country.isoCode);
      const formattedStates = states.map(s => ({
        id: s.isoCode,
        name: s.name,
        isoCode: s.isoCode,
        countryCode: s.countryCode,
        ...s
      }));
      setStateOptions(formattedStates);
    } else {
      setStateOptions([]);
      // If country is cleared, we might need to clear state in form data, 
      // but that's handled in handleCountryChange
    }
  }, [formData.country]);

  // Update city options when state changes
  useEffect(() => {
    if (formData.country && formData.address.state) {
      const cities = City.getCitiesOfState(formData.country.isoCode, formData.address.state.isoCode);
      const formattedCities = cities.map(c => ({
        id: `${c.name}-${c.stateCode}`,
        name: c.name,
        stateCode: c.stateCode,
        countryCode: c.countryCode,
        ...c
      }));
      setCityOptions(formattedCities);
    } else {
      setCityOptions([]);
    }
  }, [formData.country, formData.address.state]);

  const handleCountryChange = (value) => {
    setFormData(prev => ({
      ...prev,
      country: value,
      address: {
        ...prev.address,
        state: null,
        city: null
      }
    }));
    setValidationErrors(prev => ({
      ...prev,
      country: '',
      state: '',
      city: ''
    }));
  };

  const handleStateChange = (value) => {
    setFormData(prev => ({
      ...prev,
      address: {
        ...prev.address,
        state: value,
        city: null
      }
    }));
    setValidationErrors(prev => ({
      ...prev,
      state: '',
      city: ''
    }));
  };

  const handleCityChange = (value) => {
    setFormData(prev => ({
      ...prev,
      address: {
        ...prev.address,
        city: value
      }
    }));
    setValidationErrors(prev => ({
      ...prev,
      city: ''
    }));
  };


  // Handle form input changes
  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    const config = fieldConfig[name] || { type: 'text', limit: 100 };

    let processedValue = value;

    // Process value based on field type
    if (config.type === 'text') {
      // Text fields: only alphanumeric, spaces, comma
      processedValue = value.replace(/[^a-zA-Z0-9\s,]/g, '').substring(0, config.limit);
    } else if (config.type === 'number') {
      // Number fields: only numbers
      processedValue = value.replace(/\D/g, '').substring(0, config.limit);
    }

    if (name.includes('.')) {
      const [parent, child] = name.split('.');
      setFormData(prev => ({
        ...prev,
        [parent]: {
          ...prev[parent],
          [child]: processedValue
        }
      }));
      // Clear error when user starts typing
      if (validationErrors[child]) {
        setValidationErrors(prev => ({
          ...prev,
          [child]: ''
        }));
      }
    } else if (type === 'checkbox') {
      if (name === 'facilities') {
        setFormData(prev => ({
          ...prev,
          facilities: checked
            ? [...prev.facilities, value]
            : prev.facilities.filter(f => f !== value)
        }));
      } else {
        setFormData(prev => ({
          ...prev,
          [name]: checked
        }));
      }
    } else {
      setFormData(prev => ({
        ...prev,
        [name]: processedValue
      }));
      // Clear error when user starts typing
      if (validationErrors[name]) {
        setValidationErrors(prev => ({
          ...prev,
          [name]: ''
        }));
      }
    }
  };

  // Handle keydown for input validation
  const handleInputKeyDown = (e) => {
    const { name } = e.target;
    const config = fieldConfig[name] || { type: 'text', limit: 100 };
    const value = e.target.value;

    // Allow navigation and control keys for all fields
    if (
      [8, 9, 27, 13, 46, 35, 36, 37, 38, 39, 40].indexOf(e.keyCode) !== -1 ||
      // Allow: Ctrl+A, Ctrl+C, Ctrl+V, Ctrl+X
      (e.keyCode === 65 && e.ctrlKey === true) ||
      (e.keyCode === 67 && e.ctrlKey === true) ||
      (e.keyCode === 86 && e.ctrlKey === true) ||
      (e.keyCode === 88 && e.ctrlKey === true)
    ) {
      return;
    }

    // Check if field is at character limit
    if (value.length >= config.limit) {
      e.preventDefault();
      return;
    }

    // Field-specific character validation
    if (config.type === 'text') {
      // Text fields: only allow alphanumeric, space, comma
      const allowedKeyCodes = [
        ...Array.from({ length: 26 }, (_, i) => 65 + i), // A-Z
        ...Array.from({ length: 26 }, (_, i) => 97 + i), // a-z
        ...Array.from({ length: 10 }, (_, i) => 48 + i), // 0-9
        ...Array.from({ length: 10 }, (_, i) => 96 + i), // 0-9 (numpad)
        32, // space
        188, // comma
      ];
      if (!allowedKeyCodes.includes(e.keyCode)) {
        e.preventDefault();
      }
    } else if (config.type === 'number') {
      // Number fields: only allow numbers
      if (
        (e.shiftKey || (e.keyCode < 48 || e.keyCode > 57)) &&
        (e.keyCode < 96 || e.keyCode > 105)
      ) {
        e.preventDefault();
      }
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    // Clear previous validation errors
    clearValidationErrors();

    // Validate form
    if (!validateForm()) {
      return;
    }

    try {
      setIsSubmitting(true);

      // Generate fullAddress if it's empty
      // We need to handle objects for country, state, city
      // Always regenerate fullAddress to ensure it's up to date with form changes
      const addressParts = [];
      // if (formData.name) addressParts.push(formData.name);
      if (formData.address.street) addressParts.push(formData.address.street);
      if (formData.address.city?.name) addressParts.push(formData.address.city.name);
      if (formData.address.state?.name) addressParts.push(formData.address.state.name);
      if (formData.country?.name) addressParts.push(formData.country.name);
      if (formData.address.postalCode) addressParts.push(formData.address.postalCode);
      const fullAddress = addressParts.join(', ');

      // Clean up form data
      const submitData = {
        ...formData,
        capacity: formData.capacity ? parseInt(formData.capacity) : 0,
        coordinates: {
          latitude: formData.coordinates.latitude ? parseFloat(formData.coordinates.latitude) : null,
          longitude: formData.coordinates.longitude ? parseFloat(formData.coordinates.longitude) : null
        },
        // Transform the location objects for backend storage
        // Structure matches JobApplication.jsx pattern for consistency
        country: formData.country ? {
          id: formData.country.id || formData.country.isoCode, // Fallback if id missing
          name: formData.country.name,
          iso2: formData.country.isoCode,
          isoCode: formData.country.isoCode
        } : null,
        address: {
          ...formData.address,
          country: formData.country ? { // Include country in address object as well if backend expects it nested
            id: formData.country.id || formData.country.isoCode,
            name: formData.country.name,
            iso2: formData.country.isoCode,
            isoCode: formData.country.isoCode
          } : null,
          state: formData.address.state ? {
            id: formData.address.state.id || formData.address.state.isoCode,
            name: formData.address.state.name,
            state_code: formData.address.state.isoCode,
            isoCode: formData.address.state.isoCode
          } : null,
          city: formData.address.city ? {
            id: formData.address.city.id || formData.address.city.name, // Cities might not have iso codes always
            name: formData.address.city.name
          } : null,
          fullAddress: fullAddress
        },
        fullAddress: fullAddress
      };

      let result;
      if (showEditModal && selectedLocation) {
        result = await locationService.updateLocation(selectedLocation._id, submitData);
      } else {
        result = await locationService.createLocation(submitData);
      }

      if (result.success) {
        showSuccess(result.message || 'Location saved successfully!');
        setShowAddModal(false);
        setShowEditModal(false);
        setFormData(initialFormData);
        setValidationErrors({});
        refetchLocations();
      } else {
        const failureMessage = result.message || 'Failed to save location';
        const lowerMsg = failureMessage.toLowerCase();

        // Treat known business-rule failures (like duplicates) as warnings instead of hard errors
        if (lowerMsg.includes('already exists') || lowerMsg.includes('duplicate')) {
          showWarning(failureMessage, 'Warning');
        } else {
          showError(failureMessage || 'Failed to save location');
        }
      }
    } catch (err) {
      console.error('💥 Error saving location:', err);
      showError('Failed to save location. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  // Handle edit
  const handleEdit = (location) => {
    setSelectedLocation(location);
    // Convert HTML description to plain text with preserved line breaks
    let description = location.description || '';
    if (description) {
      // Remove HTML tags but preserve line breaks
      description = description
        .replace(/<br\s*\/?>/gi, '\n') // Convert <br> to newline
        .replace(/<\/p>/gi, '\n') // Convert </p> to newline
        .replace(/<p[^>]*>/gi, '') // Remove <p> tags
        .replace(/<[^>]*>/g, '') // Remove all other HTML tags
        .replace(/&nbsp;/g, ' ') // Convert &nbsp; to space
        .replace(/&amp;/g, '&') // Convert &amp; to &
        .replace(/&lt;/g, '<') // Convert &lt; to <
        .replace(/&gt;/g, '>') // Convert &gt; to >
        .trim();
    }
    // Helper to find location object or fallback (for edit vs old data string compatibility if needed, though we want valid objects)
    // Note: The user said "do not keep any thing for backward compatibility", but we must be able to EDIT existing records.
    // So we will try to match strings to objects if the data is just a string.

    // Hydrate Country
    let countryObj = null;
    if (location.country) {
      // If it's already an object with ID/ISO, use it
      if (typeof location.country === 'object' && location.country.isoCode) {
        countryObj = location.country;
      } else if (typeof location.country === 'string') {
        // Try to find by name
        const allCountries = Country.getAllCountries();
        const found = allCountries.find(c => c.name === location.country || c.isoCode === location.country);
        if (found) {
          countryObj = {
            id: found.isoCode,
            name: found.name,
            isoCode: found.isoCode,
            ...found
          };
        }
      }
    }

    // Hydrate State
    let stateObj = null;
    if (location.address?.state) { // Check nested address state
      if (typeof location.address.state === 'object' && location.address.state.isoCode) {
        stateObj = location.address.state;
      } else if (typeof location.address.state === 'string' && countryObj) {
        const states = State.getStatesOfCountry(countryObj.isoCode);
        const found = states.find(s => s.name === location.address.state || s.isoCode === location.address.state);
        if (found) {
          stateObj = {
            id: found.isoCode,
            name: found.name,
            isoCode: found.isoCode,
            countryCode: found.countryCode,
            ...found
          };
        }
      }
    }

    // Hydrate City
    let cityObj = null;
    if (location.address?.city) {
      if (typeof location.address.city === 'object' && location.address.city.name) {
        cityObj = location.address.city;
      } else if (typeof location.address.city === 'string' && countryObj && stateObj) {
        const cities = City.getCitiesOfState(countryObj.isoCode, stateObj.isoCode);
        const found = cities.find(c => c.name === location.address.city);
        if (found) {
          cityObj = {
            id: `${found.name}-${found.stateCode}`,
            name: found.name,
            stateCode: found.stateCode,
            countryCode: found.countryCode,
            ...found
          };
        }
      }
    }

    setFormData({
      name: location.name || '',
      code: location.code || '',
      country: countryObj,
      address: {
        street: location.address?.street || '',
        city: cityObj,
        state: stateObj,
        postalCode: location.address?.postalCode || location.address?.pincode || '',
        fullAddress: location.address?.fullAddress || ''
      },
      coordinates: {
        latitude: location.coordinates?.latitude?.toString() || '',
        longitude: location.coordinates?.longitude?.toString() || ''
      },
      capacity: location.capacity?.toString() || '',
      description: description,
      facilities: location.facilities || [],
      isActive: location.isActive !== false
    });
    setShowEditModal(true);
  };

  // Handle view
  const handleView = (location) => {
    setSelectedLocation(location);
    setShowViewModal(true);
  };

  // Handle delete confirmation
  const handleDeleteClick = (location) => {
    setSelectedLocation(location);
    setShowDeleteModal(true);
  };

  // Handle actual delete
  const handleDelete = async () => {
    if (!selectedLocation) return;

    try {
      setIsSubmitting(true);
      const result = await locationService.deleteLocation(selectedLocation._id);

      if (result.success) {
        showSuccess('Location deleted successfully!');
        refetchLocations();
        setShowDeleteModal(false);
        setSelectedLocation(null);
      } else {
        showError(result.message || 'Failed to delete location');
      }
    } catch (err) {
      console.error('💥 Error deleting location:', err);
      showError('Failed to delete location. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  // Handle bulk delete
  const handleBulkDelete = async () => {
    if (selectedRows.size === 0) return;

    try {
      setIsSubmitting(true);
      const idsToDelete = Array.from(selectedRows);
      const result = await locationService.bulkDeleteLocations(idsToDelete);

      if (result.success) {
        showSuccess(`${selectedRows.size} locations deleted successfully!`);
        setSelectedRows(new Set());
        refetchLocations();
        setShowBulkDeleteModal(false);
      } else {
        showError(result.message || 'Failed to delete locations');
      }
    } catch (err) {
      console.error('💥 Error bulk deleting locations:', err);
      showError('Failed to delete locations. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  // Row selection handlers
  const handleSelectAll = (e) => {
    if (e.target.checked) {
      const allIds = filteredLocations.map(loc => loc._id);
      setSelectedRows(new Set(allIds));
    } else {
      setSelectedRows(new Set());
    }
  };

  const handleSelectRow = (id) => {
    const newSelected = new Set(selectedRows);
    if (newSelected.has(id)) {
      newSelected.delete(id);
    } else {
      newSelected.add(id);
    }
    setSelectedRows(newSelected);
  };

  const mapLocationsForExport = (locations) => {
    return locations.map(loc => ({
      'Location Name': loc.name,
      'State': loc.address?.state?.name || loc.address?.state || '',
      'City': loc.address?.city?.name || loc.address?.city || '',
      'Country': loc.country?.name || loc.country || '',
      'Capacity': loc.capacity || 0,
      'Status': loc.isActive ? 'Active' : 'Inactive',
      'Created At': formatDate(loc.createdAt),
      'Full Address': loc.address?.fullAddress || loc.fullAddress || ''
    }));
  };

  const handleExportClick = () => {
    if (selectedRows.size > 0) {
      const locationsToExport = filteredLocations.filter(loc => selectedRows.has(loc._id));
      const exportData = mapLocationsForExport(locationsToExport);
      exportToCSV(exportData, 'locations', {
        title: 'Location List',
        type: 'Selected Locations'
      });
    } else {
      setShowExportModal(true);
    }
  };

  const handleExportCurrentPage = () => {
    const exportData = mapLocationsForExport(filteredLocations);
    exportToCSV(exportData, 'locations_page', {
      title: 'Location List',
      type: 'Current Page'
    });
    setShowExportModal(false);
  };

  const handleExportRange = async (startDate, endDate) => {
    setExportLoading(true);
    try {
      const queryParams = new URLSearchParams();
      queryParams.append("type", `Date Range (${startDate} to ${endDate})`);
      if (startDate) queryParams.append("startDate", startDate);
      if (endDate) queryParams.append("endDate", endDate);
      if (debouncedSearchTerm) queryParams.append("search", debouncedSearchTerm);
      if (filters.isActive !== "all") queryParams.append("isActive", filters.isActive === 'active' ? 'true' : 'false');

      const url = `${process.env.REACT_APP_API_BASE_URL}/locations/export?${queryParams.toString()}`;
      const filename = generateExportFilename('locations_range');
      await downloadFromBackend(url, filename);

      setShowExportModal(false);
      showSuccess('Export started successfully');
    } catch (error) {
      console.error("Export failed:", error);
      showError("An error occurred during export");
    } finally {
      setExportLoading(false);
    }
  };

  const handleExportAll = async () => {
    setExportLoading(true);
    try {
      const queryParams = new URLSearchParams();
      queryParams.append("type", "All Data");
      if (debouncedSearchTerm) queryParams.append("search", debouncedSearchTerm);
      if (filters.isActive !== "all") queryParams.append("isActive", filters.isActive === 'active' ? 'true' : 'false');

      const url = `${process.env.REACT_APP_API_BASE_URL}/locations/export?${queryParams.toString()}`;
      const filename = generateExportFilename('all_locations');
      await downloadFromBackend(url, filename);

      setShowExportModal(false);
      showSuccess('All locations export started');
    } catch (error) {
      console.error("Export failed:", error);
      showError("An error occurred during export");
    } finally {
      setExportLoading(false);
    }
  };

  // Error state
  if (error) {
    return (
      <div className="bg-red-50 border border-red-200 rounded-lg p-6 text-center">
        <div className="text-red-600 mb-4">
          <h3 className="text-lg font-semibold">Error Loading Locations</h3>
          <p className="text-sm">{error}</p>
        </div>
        <PermissionGate permission="location_management" action="read">
          <button
            onClick={() => refetchLocations()}
            className="btn-danger"
          >
            Try Again
          </button>
        </PermissionGate>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div className="flex items-center gap-4">
          <div className="bg-slate-900 p-2.5 rounded-xl shadow-sm border border-slate-800 flex items-center justify-center">
            <MapPin className="h-6 w-6 text-white" />
          </div>
          <div>
            <h1 className="text-2xl sm:text-3xl font-bold text-slate-800 tracking-tight">Location Management</h1>
            <p className="text-sm sm:text-base text-slate-500 mt-1">Manage office locations and work sites</p>
          </div>
        </div>
        <div className="flex flex-wrap items-center gap-2">
            <PermissionGate permission="location_management" action="delete">
              {selectedRows.size > 0 && (
                <button
                  onClick={() => setShowBulkDeleteModal(true)}
                  className="flex items-center justify-center space-x-1.5 !bg-red-600 hover:!bg-red-700 !text-white rounded-md px-3 py-1.5 text-[13px] font-medium h-8 shadow-sm transition-colors"
                >
                  <Trash2 className="h-4 w-4" />
                  <span>Delete Selected ({selectedRows.size})</span>
                </button>
              )}
            </PermissionGate>
            <PermissionGate permission="location_management" action="read">
              <button
                onClick={handleExportClick}
                disabled={exportLoading}
                className="bg-white border border-slate-300 hover:bg-slate-50 text-slate-700 shadow-sm transition-colors rounded-md px-3 py-1.5 text-[13px] font-medium flex items-center justify-center space-x-1.5 h-8 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {exportLoading ? <div className="animate-spin rounded-full h-4 w-4 border-2 border-slate-300 border-t-slate-700" /> : <Download className="h-4 w-4" />}
                <span>{selectedRows.size > 0 ? `Export Selected (${selectedRows.size})` : "Export"}</span>
              </button>
            </PermissionGate>
            <PermissionGate permission="location_management" action="create">
              <button
                onClick={() => {
                  setFormData(initialFormData);
                  setValidationErrors({});
                  setShowAddModal(true);
                }}
                className="bg-slate-900 hover:bg-slate-800 text-white shadow-sm transition-colors rounded-md px-3 py-1.5 text-[13px] font-medium flex items-center justify-center space-x-1.5 h-8"
              >
                <Plus className="h-4 w-4" />
                <span>Add Location</span>
              </button>
            </PermissionGate>
        </div>
      </div>


      {/* Search and Status Filter */}
      <div className="bg-white rounded-lg border border-gray-200 p-6">
        <div className="flex items-center space-x-4">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 h-4 w-4" />
            <input
              type="text"
              placeholder="Search by Name, Code, City, State, Country or Postal Code..."
              autoFocus={true}
              value={searchTerm}
              onChange={(e) => {
                // Allow alphanumeric, spaces, comma, and email special characters (@, ., -, _, +)
                const value = e.target.value.replace(/[^a-zA-Z0-9\s,@._+-]/g, '').slice(0, 100);
                setSearchTerm(value);
              }}
              onKeyPress={(e) => {
                // Allow alphanumeric, spaces, comma, and email special characters (@, ., -, _, +)
                if (!/^[a-zA-Z0-9\s,@._+-]$/.test(e.key) && !['Backspace', 'Delete', 'Tab', 'Enter', 'ArrowLeft', 'ArrowRight'].includes(e.key)) {
                  e.preventDefault();
                }
              }}
              maxLength={100}
              className="input-field pl-10 pr-10"
            />
            {searchTerm && (
              <button
                onClick={() => setSearchTerm("")}
                className="absolute right-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400 hover:text-gray-600 transition-colors"
              >
                <X className="h-4 w-4" />
              </button>
            )}
          </div>
          <div>
            <select
              value={filters.isActive}
              onChange={(e) => setFilters(prev => ({ ...prev, isActive: e.target.value }))}
              className="input-field w-auto"
            >
              <option value="all">All Status</option>
              <option value="active">Active</option>
              <option value="inactive">Inactive</option>
            </select>
          </div>
          <DateRangeFilter
            startDate={dateRange.startDate}
            endDate={dateRange.endDate}
            onChange={setDateRange}
          />

          {/* Clear Filters */}
          {(searchTerm || filters.isActive !== "all" || dateRange.startDate || dateRange.endDate) && (
            <div className="relative flex-shrink-0">
              <Button
                variant="outline"
                icon={<X className="h-4 w-4" />}
                onClick={() => {
                  setSearchTerm("");
                  setFilters({ isActive: 'all' });
                  setDateRange({ startDate: '', endDate: '' });
                }}
                className="h-[42px]"
              >
                Clear
              </Button>
            </div>
          )}
        </div>
      </div>

      {/* Locations Table */}
      <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-3 py-3 text-left w-10">
                  <input
                    type="checkbox"
                    className="rounded border-gray-300 text-[#0F2942] focus:ring-[#0F2942]/50 h-3 w-3"
                    checked={filteredLocations.length > 0 && selectedRows.size === filteredLocations.length}
                    onChange={handleSelectAll}
                  />
                </th>
                <th className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer group hover:bg-gray-100 transition-colors"
                  onClick={() => requestSort('name')}>
                  <div className="flex items-center">
                    Location {getSortIcon('name')}
                  </div>
                </th>
                <th className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer group hover:bg-gray-100 transition-colors"
                  onClick={() => requestSort('state')}>
                  <div className="flex items-center">
                    State {getSortIcon('state')}
                  </div>
                </th>
                <th className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer group hover:bg-gray-100 transition-colors"
                  onClick={() => requestSort('capacity')}>
                  <div className="flex items-center">
                    Capacity {getSortIcon('capacity')}
                  </div>
                </th>
                <th className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer group hover:bg-gray-100 transition-colors"
                  onClick={() => requestSort('isActive')}>
                  <div className="flex items-center">
                    Status {getSortIcon('isActive')}
                  </div>
                </th>
                <th className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer group hover:bg-gray-100 transition-colors"
                  onClick={() => requestSort('createdAt')}>
                  <div className="flex items-center">
                    Created At {getSortIcon('createdAt')}
                  </div>
                </th>
                <th className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer group hover:bg-gray-100 transition-colors"
                  onClick={() => requestSort('updatedAt')}>
                  <div className="flex items-center">
                    Updated At {getSortIcon('updatedAt')}
                  </div>
                </th>
                <th className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {loading ? (
                [...Array(5)].map((_, index) => (
                  <tr key={index} className="animate-pulse">
                    <td className="px-3 py-4 whitespace-nowrap">
                      <div className="h-3 bg-gray-200 rounded w-3"></div>
                    </td>
                    <td className="px-6 py-4">
                      <div className="flex items-center">
                        <div className="rounded-full bg-gray-200 h-8 w-8 mr-3"></div>
                        <div className="h-4 bg-gray-200 rounded w-32"></div>
                      </div>
                    </td>
                    <td className="px-6 py-4">
                      <div className="h-4 bg-gray-200 rounded w-24"></div>
                    </td>
                    <td className="px-6 py-4">
                      <div className="h-4 bg-gray-200 rounded w-12"></div>
                    </td>
                    <td className="px-6 py-4">
                      <div className="h-4 bg-gray-200 rounded w-16"></div>
                    </td>
                    <td className="px-6 py-4">
                      <div className="h-4 bg-gray-200 rounded w-20"></div>
                    </td>
                    <td className="px-6 py-4">
                      <div className="flex space-x-2">
                        <div className="h-4 bg-gray-200 rounded w-4"></div>
                        <div className="h-4 bg-gray-200 rounded w-4"></div>
                        <div className="h-4 bg-gray-200 rounded w-4"></div>
                      </div>
                    </td>
                  </tr>
                ))
              ) : filteredLocations.length === 0 ? (
                <tr>
                  <td colSpan="7" className="px-6 py-12 text-center">
                    <div className="text-gray-500">
                      <MapPin className="h-12 w-12 mx-auto mb-4 text-gray-300" />
                      <h3 className="text-lg font-medium mb-2">No locations found</h3>
                      <p className="text-sm">
                        {searchTerm || filters.isActive !== 'all'
                          ? 'Try adjusting your search or status filter'
                          : 'Get started by adding your first location'
                        }
                      </p>
                    </div>
                  </td>
                </tr>
              ) : (
                filteredLocations.map((location) => (
                  <tr key={location._id} className={`hover:bg-gray-50 ${selectedRows.has(location._id) ? 'bg-blue-50/50' : ''}`}>
                    <td className="px-3 py-4 whitespace-nowrap">
                      <input
                        type="checkbox"
                        className="rounded border-gray-300 text-[#0F2942] focus:ring-[#0F2942]/50 h-3 w-3"
                        checked={selectedRows.has(location._id)}
                        onChange={() => handleSelectRow(location._id)}
                      />
                    </td>
                    <td className="px-3 py-4 max-w-xs">
                      <div className="flex items-center min-w-0">
                        <div className="flex-shrink-0 h-8 w-8">
                          <div className="h-8 w-8 rounded-full bg-blue-100 flex items-center justify-center">
                            <Building className="h-4 w-4 text-[#0F2942]" />
                          </div>
                        </div>
                        <div className="ml-3 min-w-0 flex-1">
                          <div className="text-sm font-medium text-gray-900 capitalize truncate" title={location.name}>
                            {location.name}
                          </div>
                        </div>
                      </div>
                    </td>
                    <td className="px-3 py-4 max-w-xs">
                      <div className="text-sm text-gray-900 capitalize truncate" title={location.address?.state?.name || 'N/A'}>
                        {location.address?.state?.name || 'N/A'}
                      </div>
                    </td>
                    <td className="px-3 py-4 whitespace-nowrap text-sm text-gray-900">
                      {location.capacity || 0}
                    </td>
                    <td className="px-3 py-4 whitespace-nowrap">
                      <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${location.isActive
                        ? 'bg-green-100 text-green-800'
                        : 'bg-red-100 text-red-800'
                        }`}>
                        {location.isActive ? 'Active' : 'Inactive'}
                      </span>
                    </td>
                    <td className="px-3 py-4 whitespace-nowrap">
                      <div className="text-sm text-gray-900">
                        {formatDate(location.createdAt) || 'N/A'}
                      </div>
                    </td>
                    <td className="px-3 py-4 whitespace-nowrap">
                      <div className="text-sm text-gray-900">
                        {location.updatedAt ? formatDate(location.updatedAt) : 'N/A'}
                      </div>
                    </td>
                    <td className="px-3 py-4 whitespace-nowrap text-sm font-medium">
                      <div className="flex items-center space-x-2">
                        <PermissionGate permission="location_management" action="read">
                          <button
                            onClick={() => handleView(location)}
                            className="text-green-600 hover:text-green-800 transition-colors"
                            title="View"
                          >
                            <Eye className="h-4 w-4" />
                          </button>
                        </PermissionGate>
                        <PermissionGate permission="location_management" action="update">
                          <button
                            onClick={() => handleEdit(location)}
                            className="text-[#0F2942] hover:text-blue-800 transition-colors"
                            title="Edit"
                          >
                            <Edit className="h-4 w-4" />
                          </button>
                        </PermissionGate>
                        <PermissionGate permission="location_management" action="delete">
                          <button
                            onClick={() => handleDeleteClick(location)}
                            className="text-red-600 hover:text-red-800 transition-colors"
                            title="Delete"
                          >
                            <Trash2 className="h-4 w-4" />
                          </button>
                        </PermissionGate>
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>
      {
        showPaginationControls && (
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between px-6 py-4 border-t border-gray-200 space-y-3 sm:space-y-0 bg-white rounded-b-lg">
            <div className="text-sm text-gray-600">
              {pagination.totalCount > 0
                ? `Showing ${startItem}-${endItem} of ${pagination.totalCount} locations`
                : 'No locations to display'}
            </div>
            <div className="flex flex-col sm:flex-row sm:items-center sm:space-x-4 space-y-3 sm:space-y-0">
              <div className="flex items-center space-x-2">
                <span className="text-sm text-gray-600">Rows per page</span>
                <select
                  value={pageSize}
                  onChange={handlePageSizeChange}
                  className="input-field w-auto py-1 px-2 text-sm"
                >
                  {[10, 25, 50, 100].map((size) => (
                    <option key={size} value={size}>
                      {size}
                    </option>
                  ))}
                </select>
              </div>
              <div className="flex items-center space-x-3">
                <span className="text-sm text-gray-600">
                  Page {pagination.currentPage} of {totalPages}
                </span>
                <div className="flex items-center space-x-2">
                  <button
                    type="button"
                    onClick={() => setCurrentPage((prev) => Math.max(prev - 1, 1))}
                    disabled={!canGoPrev || loading}
                    className={`px-3 py-1 text-sm border rounded-md ${!canGoPrev || loading
                      ? 'border-gray-200 text-gray-400 cursor-not-allowed'
                      : 'border-gray-300 text-gray-700 hover:bg-gray-50'
                      }`}
                  >
                    Previous
                  </button>
                  <button
                    type="button"
                    onClick={() => setCurrentPage((prev) => Math.min(prev + 1, totalPages))}
                    disabled={!canGoNext || loading}
                    className={`px-3 py-1 text-sm border rounded-md ${!canGoNext || loading
                      ? 'border-gray-200 text-gray-400 cursor-not-allowed'
                      : 'border-gray-300 text-gray-700 hover:bg-gray-50'
                      }`}
                  >
                    Next
                  </button>
                </div>
              </div>
            </div>
          </div>
        )
      }

      {/* Add/Edit Location Modal */}
      {
        (showAddModal || showEditModal) && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 !mt-0">
            <div className="bg-white rounded-lg p-6 max-w-2xl w-full mx-4 max-h-[90vh] flex flex-col overflow-hidden">
              <div className="flex items-center justify-between mb-4 flex-shrink-0">
                <h3 className="text-lg font-semibold text-gray-900">
                  {showEditModal ? 'Edit Location' : 'Add Location'}
                </h3>
                <button
                  onClick={() => {
                    setShowAddModal(false);
                    setShowEditModal(false);
                    setSelectedLocation(null);
                    setValidationErrors({});
                    setFormData(initialFormData);
                  }}
                  className="text-gray-400 hover:text-gray-600"
                >
                  <span className="sr-only">Close</span>
                  <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>

              <form onSubmit={handleSubmit} className="flex flex-col flex-1 overflow-hidden" noValidate>
                <div className="space-y-4 flex-1 overflow-y-auto pr-1 p-1">
                  <div className="grid grid-cols-1 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Name <span className="text-red-500">*</span>
                      </label>
                      <input
                        type="text"
                        name="name"
                        value={formData.name}
                        onChange={handleInputChange}
                        onKeyDown={handleInputKeyDown}
                        onBlur={handleFieldBlur}
                        className={getFieldClassName('name')}
                        placeholder="e.g., Mumbai Office"
                      />
                      <div className="flex justify-between items-center mt-1">
                        {getFieldError('name') && (
                          <p className="text-sm text-red-600 flex items-center">
                            <AlertCircle className="h-4 w-4 mr-1" />
                            {getFieldError('name')}
                          </p>
                        )}
                        <p className="text-xs text-gray-500 ml-auto">
                          {formData.name.length}/{fieldConfig.name.limit} characters
                        </p>
                      </div>
                    </div>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Country
                      </label>
                      <LocationSelect
                        options={countryOptions}
                        value={formData.country}
                        onChange={handleCountryChange}
                        onBlur={() => handleFieldBlur({ target: { name: 'country' } })}
                        placeholder="Select Country"
                        error={!!validationErrors.country}
                        className="w-full"
                      />
                      {getFieldError('country') && (
                        <p className="text-sm text-red-600 mt-1 flex items-center">
                          <AlertCircle className="h-4 w-4 mr-1" />
                          {getFieldError('country')}
                        </p>
                      )}
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        State
                      </label>
                      <LocationSelect
                        options={stateOptions}
                        value={formData.address.state}
                        onChange={handleStateChange}
                        onBlur={() => handleFieldBlur({ target: { name: 'state' } })}
                        placeholder="Select State"
                        disabled={!formData.country}
                        error={!!validationErrors.state}
                        className="w-full"
                      />
                      {getFieldError('state') && (
                        <p className="text-sm text-red-600 mt-1 flex items-center">
                          <AlertCircle className="h-4 w-4 mr-1" />
                          {getFieldError('state')}
                        </p>
                      )}
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        City
                      </label>
                      <LocationSelect
                        options={cityOptions}
                        value={formData.address.city}
                        onChange={handleCityChange}
                        onBlur={() => handleFieldBlur({ target: { name: 'city' } })}
                        placeholder="Select City"
                        disabled={!formData.address.state}
                        error={!!validationErrors.city}
                        className="w-full"
                      />
                      {getFieldError('city') && (
                        <p className="text-sm text-red-600 mt-1 flex items-center">
                          <AlertCircle className="h-4 w-4 mr-1" />
                          {getFieldError('city')}
                        </p>
                      )}
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Street Address
                      </label>
                      <input
                        type="text"
                        name="address.street"
                        value={formData.address.street}
                        onChange={handleInputChange}
                        onKeyDown={handleInputKeyDown}
                        onBlur={handleFieldBlur}
                        className={getFieldClassName('street')}
                        placeholder="Street, Building, Floor"
                      />
                      <div className="flex justify-between items-center mt-1">
                        {getFieldError('street') && (
                          <p className="text-sm text-red-600 flex items-center">
                            <AlertCircle className="h-4 w-4 mr-1" />
                            {getFieldError('street')}
                          </p>
                        )}
                        <p className="text-xs text-gray-500 ml-auto">
                          {formData.address.street.length}/{fieldConfig['address.street'].limit} chars
                        </p>
                      </div>
                    </div>
                  </div>

                  <div className="grid grid-cols-1 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Capacity
                      </label>
                      <input
                        type="text"
                        name="capacity"
                        value={formData.capacity}
                        onChange={handleInputChange}
                        onKeyDown={handleInputKeyDown}
                        onBlur={handleFieldBlur}
                        className={getFieldClassName('capacity')}
                        placeholder="e.g., 100"
                      />
                      <div className="flex justify-between items-center mt-1">
                        {getFieldError('capacity') && (
                          <p className="text-sm text-red-600 flex items-center">
                            <AlertCircle className="h-4 w-4 mr-1" />
                            {getFieldError('capacity')}
                          </p>
                        )}
                        <p className="text-xs text-gray-500 ml-auto">
                          {formData.capacity.length}/{fieldConfig.capacity.limit} digits
                        </p>
                      </div>
                    </div>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Postal Code
                    </label>
                    <input
                      type="text"
                      name="address.postalCode"
                      value={formData.address.postalCode}
                      onChange={handleInputChange}
                      onKeyDown={handleInputKeyDown}
                      onBlur={handleFieldBlur}
                      className={getFieldClassName('postalCode')}
                      placeholder="e.g., 400001"
                    />
                    <div className="flex justify-between items-center mt-1">
                      {getFieldError('postalCode') && (
                        <p className="text-sm text-red-600 flex items-center">
                          <AlertCircle className="h-4 w-4 mr-1" />
                          {getFieldError('postalCode')}
                        </p>
                      )}
                      <p className="text-xs text-gray-500 ml-auto">
                        {formData.address.postalCode.length}/{fieldConfig['address.postalCode'].limit} digits
                      </p>
                    </div>
                  </div>

                  <div className="flex items-center">
                    <input
                      type="checkbox"
                      name="isActive"
                      checked={formData.isActive}
                      onChange={handleInputChange}
                      className="h-4 w-4 text-agrosaf-green focus:ring-agrosaf-green border-gray-300 rounded"
                    />
                    <label className="ml-2 text-sm text-gray-700">
                      Active Location
                    </label>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Description
                    </label>
                    <textarea
                      name="description"
                      value={formData.description}
                      onChange={(e) => {
                        const value = e.target.value;
                        // Limit to 500 characters
                        if (value.length > 500) {
                          setValidationErrors(prev => ({
                            ...prev,
                            description: 'Description cannot exceed 500 characters'
                          }));
                          return;
                        }
                        setFormData(prev => ({
                          ...prev,
                          description: value
                        }));
                        // Clear error when user starts typing
                        if (validationErrors.description) {
                          setValidationErrors(prev => ({
                            ...prev,
                            description: ''
                          }));
                        }
                      }}
                      rows="4"
                      maxLength={500}
                      className={getFieldClassName('description')}
                      placeholder="Brief description of the location..."
                    />
                    <div className="flex justify-between items-center mt-1">
                      {getFieldError('description') && (
                        <p className="text-sm text-red-600 flex items-center">
                          <AlertCircle className="h-4 w-4 mr-1" />
                          {getFieldError('description')}
                        </p>
                      )}
                      <p className="text-xs text-gray-500 ml-auto">
                        {formData.description.length}/500 characters
                      </p>
                    </div>
                  </div>
                </div>





                <div className="flex justify-end space-x-3 pt-4 border-t border-gray-200 mt-4">
                  <PermissionGate permission="location_management" action="read">
                    <button
                      type="button"
                      onClick={() => {
                        setShowAddModal(false);
                        setShowEditModal(false);
                        setSelectedLocation(null);
                        setValidationErrors({});
                        setFormData(initialFormData);
                      }}
                      className="btn-cancel"
                    >
                      Cancel
                    </button>
                  </PermissionGate>
                  <PermissionGate permission="location_management" action={showEditModal ? "update" : "create"}>
                    <button
                      type="submit"
                      disabled={isSubmitting}
                      className="btn-create disabled:opacity-50"
                    >
                      {isSubmitting ? 'Saving...' : (showEditModal ? 'Update Location' : 'Add Location')}
                    </button>
                  </PermissionGate>
                </div>
              </form>
            </div >
          </div >
        )
      }

      {/* View Location Modal */}
      {
        showViewModal && selectedLocation && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 !mt-0">
            <div className="bg-white rounded-lg p-10 max-w-3xl w-full mx-4 max-h-[90vh] overflow-y-auto">
              <div className="flex items-center justify-between mb-6">
                <h3 className="text-2xl font-semibold text-gray-900 flex items-center">
                  <MapPin className="h-6 w-6 mr-2 text-agrosaf-green" />
                  Location Details
                </h3>
                <PermissionGate permission="location_management" action="read">
                  <button
                    onClick={() => {
                      setShowViewModal(false);
                      setSelectedLocation(null);
                    }}
                    className="text-gray-400 hover:text-gray-600"
                  >
                    <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                    </svg>
                  </button>
                </PermissionGate>
              </div>

              <div className="space-y-6">
                {/* Basic Information */}
                <div className="border-b border-gray-200 pb-4">
                  <h4 className="text-lg font-semibold text-gray-900 mb-4 border-b pb-2">Basic Information</h4>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-500 mb-1 capitalize">Location Name</label>
                      <p className="text-sm text-gray-900 capitalize break-words whitespace-pre-wrap">
                        {selectedLocation.name || 'N/A'}
                      </p>
                    </div>
                    {/* Country moved to Address Information */}
                    <div>
                      <label className="block text-sm font-medium text-gray-500 mb-1 capitalize">Capacity</label>
                      <p className="text-sm text-gray-900">{selectedLocation.capacity || 0}</p>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-500 mb-1 capitalize">Status</label>
                      <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full capitalize ${selectedLocation.isActive
                        ? 'bg-green-100 text-green-800'
                        : 'bg-red-100 text-red-800'
                        }`}>
                        {selectedLocation.isActive ? 'Active' : 'Inactive'}
                      </span>
                    </div>
                  </div>
                </div>

                {/* Address Information */}
                <div className="border-b border-gray-200 pb-4">
                  <h4 className="text-lg font-semibold text-gray-900 mb-4 border-b pb-2">Address Information</h4>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div className="md:col-span-2">
                      <label className="block text-sm font-medium text-gray-500 mb-1 capitalize">Full Address</label>
                      <p className="text-sm text-gray-900 capitalize break-words whitespace-pre-wrap">
                        {selectedLocation.fullAddress || selectedLocation.address?.fullAddress || 'N/A'}
                      </p>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-500 mb-1 capitalize">Street</label>
                      <p className="text-sm text-gray-900 capitalize break-words whitespace-pre-wrap">
                        {selectedLocation.address?.street || 'N/A'}
                      </p>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-500 mb-1 capitalize">City</label>
                      <p className="text-sm text-gray-900 capitalize break-words whitespace-pre-wrap">
                        {selectedLocation.address?.city?.name || selectedLocation.address?.city || 'N/A'}
                      </p>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-500 mb-1 capitalize">State</label>
                      <p className="text-sm text-gray-900 capitalize break-words whitespace-pre-wrap">
                        {selectedLocation.address?.state?.name || selectedLocation.address?.state || 'N/A'}
                      </p>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-500 mb-1 capitalize">Country</label>
                      <p className="text-sm text-gray-900 capitalize">{selectedLocation.country?.name || selectedLocation.country || 'N/A'}</p>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-500 mb-1 capitalize">Postal Code</label>
                      <p className="text-sm text-gray-900 break-words whitespace-pre-wrap">
                        {selectedLocation.address?.postalCode || selectedLocation.address?.pincode || 'N/A'}
                      </p>
                    </div>
                  </div>
                </div>

                {/* Facilities */}
                {selectedLocation.facilities && selectedLocation.facilities.length > 0 && (
                  <div className="border-b border-gray-200 pb-4">
                    <h4 className="text-lg font-semibold text-gray-900 mb-4 border-b pb-2">Facilities</h4>
                    <div className="flex flex-wrap gap-2">
                      {selectedLocation.facilities.map((facility, index) => (
                        <span
                          key={index}
                          className="inline-flex px-3 py-1 text-sm font-medium bg-blue-100 text-blue-800 rounded-full capitalize"
                        >
                          {facility}
                        </span>
                      ))}
                    </div>
                  </div>
                )}

                {/* Description */}
                {selectedLocation.description && (
                  <div>
                    <h4 className="text-lg font-semibold text-gray-900 mb-4 border-b pb-2">Description</h4>
                    <div
                      className="text-sm text-gray-900 whitespace-pre-wrap break-words"
                      style={{ whiteSpace: 'pre-wrap' }}
                    >
                      {selectedLocation.description}
                    </div>
                  </div>
                )}

                {/* Timestamps */}
                {(selectedLocation.createdAt || selectedLocation.updatedAt) && (
                  <div>
                    <h4 className="text-lg font-semibold text-gray-900 mb-4 border-b pb-2">Timestamps</h4>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      {selectedLocation.createdAt && (
                        <div>
                          <label className="block text-sm font-medium text-gray-500 mb-1">Created At</label>
                          <p className="text-sm text-gray-900">
                            {formatDate(selectedLocation.createdAt)}
                          </p>
                        </div>
                      )}
                      {selectedLocation.updatedAt && (
                        <div>
                          <label className="block text-sm font-medium text-gray-500 mb-1">Updated At</label>
                          <p className="text-sm text-gray-900">
                            {formatDate(selectedLocation.updatedAt)}
                          </p>
                        </div>
                      )}
                    </div>
                  </div>
                )}
              </div>

              {/* Close Button */}
              <div className="flex justify-end mt-6 pt-4 border-t border-gray-200">
                <PermissionGate permission="location_management" action="read">
                  <button
                    onClick={() => {
                      setShowViewModal(false);
                      setSelectedLocation(null);
                    }}
                    className="btn-cancel"
                  >
                    Close
                  </button>
                </PermissionGate>
              </div>
            </div>
          </div>
        )
      }

      {/* Delete Confirmation Modal */}
      {
        showDeleteModal && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 !mt-0">
            <div className="bg-white rounded-lg p-6 max-w-md w-full mx-4">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-semibold text-gray-900">
                  Confirm Delete
                </h3>
                <PermissionGate permission="location_management" action="read">
                  <button
                    onClick={() => {
                      setShowDeleteModal(false);
                      setSelectedLocation(null);
                    }}
                    className="text-gray-400 hover:text-gray-600"
                  >
                    <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                    </svg>
                  </button>
                </PermissionGate>
              </div>

              <div className="mb-6">
                <p className="text-gray-600">
                  Are you sure you want to delete the location <strong className="capitalize">"{selectedLocation?.name}"</strong>?
                </p>
                <p className="text-sm text-gray-500 mt-2">
                  This action cannot be undone.
                </p>
              </div>

              <div className="flex justify-end space-x-3">
                <PermissionGate permission="location_management" action="read">
                  <button
                    onClick={() => {
                      setShowDeleteModal(false);
                      setSelectedLocation(null);
                    }}
                    className="btn-cancel"
                  >
                    Cancel
                  </button>
                </PermissionGate>
                <PermissionGate permission="location_management" action="delete">
                  <button
                    onClick={handleDelete}
                    className="btn-danger disabled:opacity-50"
                    disabled={isSubmitting}
                  >
                    {isSubmitting ? 'Deleting...' : 'Delete'}
                  </button>
                </PermissionGate>
              </div>
            </div>
          </div>
        )
      }

      {/* Bulk Delete Confirmation Modal */}
      {
        showBulkDeleteModal && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 !mt-0">
            <div className="bg-white rounded-lg p-6 max-w-md w-full mx-4">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-semibold text-gray-900">
                  Confirm Bulk Delete
                </h3>
                <PermissionGate permission="location_management" action="read">
                  <button
                    onClick={() => {
                      setShowBulkDeleteModal(false);
                    }}
                    className="text-gray-400 hover:text-gray-600"
                  >
                    <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                    </svg>
                  </button>
                </PermissionGate>
              </div>

              <div className="mb-6">
                <p className="text-gray-600">
                  Are you sure you want to delete <strong>{selectedRows.size}</strong> selected locations?
                </p>
                <p className="text-sm text-gray-500 mt-2">
                  This action cannot be undone and will permanently remove these records.
                </p>
              </div>

              <div className="flex justify-end space-x-3">
                <PermissionGate permission="location_management" action="read">
                  <button
                    onClick={() => {
                      setShowBulkDeleteModal(false);
                    }}
                    className="btn-cancel"
                  >
                    Cancel
                  </button>
                </PermissionGate>
                <PermissionGate permission="location_management" action="delete">
                  <button
                    onClick={handleBulkDelete}
                    className="btn-danger disabled:opacity-50"
                    disabled={isSubmitting}
                  >
                    {isSubmitting ? 'Deleting...' : 'Delete Selected'}
                  </button>
                </PermissionGate>
              </div>
            </div>
          </div>
        )
      }

      {/* Export Modal */}
      <ExportModal
        isOpen={showExportModal}
        onClose={() => setShowExportModal(false)}
        onExportCurrentPage={handleExportCurrentPage}
        onExportAll={handleExportAll}
        onExportRange={handleExportRange}
        loading={exportLoading}
        title="Export Locations"
        showDateRange={true}
      />

      {/* Custom Alert Component */}

    </div >
  );
};

export default LocationManagement;
